import React, { useEffect, useState, useCallback } from 'react';
import { AcademicCapIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { academyAPI } from '../../utils/api';

const STUDY_LEVELS = [
  { value: 'undergraduate', label: 'Undergraduate' },
  { value: 'postgraduate', label: 'Postgraduate' },
  { value: 'professional', label: 'Professional (e.g. bar / LPC)' },
  { value: 'other', label: 'Other' },
];

const StudentProfile = () => {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ institution: '', academic_email: '', study_level: 'undergraduate', jurisdiction: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { type, message }

  const hydrate = useCallback((data) => {
    setProfile(data);
    setForm({
      institution: data.institution || '',
      academic_email: data.academic_email || '',
      study_level: data.study_level || 'undergraduate',
      jurisdiction: data.jurisdiction || '',
    });
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await academyAPI.getProfile();
        if (active) hydrate(res.data || {});
      } catch {
        if (active) setNotice({ type: 'error', message: 'Could not load your profile.' });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [hydrate]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setNotice(null);
    try {
      const res = await academyAPI.updateProfile(form);
      hydrate(res.data || {});
      setNotice({ type: 'success', message: 'Profile saved.' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save your profile. Please check the fields and try again.';
      setNotice({ type: 'error', message: typeof msg === 'string' ? msg : 'Could not save your profile.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-2xl">
      <div className="mb-6 flex items-center gap-2">
        <AcademicCapIcon className="h-6 w-6 text-primary-500" />
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Student Profile</h1>
      </div>

      {notice && (
        <div
          className={`mb-4 p-3 rounded-md text-sm border ${
            notice.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 border-red-200 dark:border-red-800'
          }`}
        >
          {notice.message}
        </div>
      )}

      {/* Verification status */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <CheckBadgeIcon className={`h-6 w-6 flex-shrink-0 ${profile?.academic_email_verified ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'}`} />
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {profile?.academic_email_verified ? 'Academic email verified' : 'Academic email not verified'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Verifying your academic email raises your fair-use limits for the tutor, quizzes, and mock trials.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 sm:p-5 space-y-5">
        <div>
          <label htmlFor="institution" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Institution</label>
          <input
            id="institution"
            type="text"
            value={form.institution}
            onChange={(e) => setForm((f) => ({ ...f, institution: e.target.value }))}
            placeholder="e.g. University of Zimbabwe"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="academic_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Academic email</label>
          <input
            id="academic_email"
            type="email"
            value={form.academic_email}
            onChange={(e) => setForm((f) => ({ ...f, academic_email: e.target.value }))}
            placeholder="you@university.ac.zw"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Use your institutional email to unlock higher limits.</p>
        </div>

        <div>
          <label htmlFor="study_level" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Study level</label>
          <select
            id="study_level"
            value={form.study_level}
            onChange={(e) => setForm((f) => ({ ...f, study_level: e.target.value }))}
            className="mt-1 block w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
          >
            {STUDY_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>

        <div>
          <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Jurisdiction</label>
          <input
            id="jurisdiction"
            type="text"
            value={form.jurisdiction}
            onChange={(e) => setForm((f) => ({ ...f, jurisdiction: e.target.value }))}
            placeholder="e.g. Zimbabwe"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
          />
        </div>

        <div>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex justify-center items-center px-5 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentProfile;
